import React, { useEffect, useMemo, useState } from "react";
import { Search, X, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ROLE_LABEL = {
  ekyc_staff: "EKYC Staff",
  member: "Agency",
  team_staff: "Team Staff",
  resend_staff: "Resend Staff",
  delivery_man: "Delivery Man",
};
const ROLE_CLS = { ekyc_staff: "bg-[#2F6F8F]/10 text-[#2F6F8F]", member: "bg-[#8A6046]/10 text-[#8A6046]" };

export function useEkycUsers() {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    api.get("/ekyc/users").then(({ data }) => setUsers(data.users)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const byId = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);
  return { users, byId };
}

const RoleBadge = ({ role }) => (
  <span className={`shrink-0 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${ROLE_CLS[role] || "bg-[#F0F0EA] text-[#6B6B61]"}`}>
    {ROLE_LABEL[role] || role}
  </span>
);

export function MemberList({ ids, byId, onRemove }) {
  if (!ids.length) return <div className="text-xs text-[#6B6B61]" data-testid="ekyc-members-empty">Abhi koi member nahi.</div>;
  return (
    <div className="divide-y divide-[#F0F0EA] border border-[#EDEDE5] rounded" data-testid="ekyc-members-list">
      {ids.map((id) => {
        const u = byId[id];
        return (
          <div key={id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm min-w-0" data-testid={`ekyc-member-${id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{u ? u.display_name || u.username : "Unknown user"}</div>
              {u && <div className="text-[11px] text-[#6B6B61] font-mono truncate">@{u.username}</div>}
            </div>
            {u && <RoleBadge role={u.role} />}
            <button onClick={() => onRemove(id)} data-testid={`ekyc-member-remove-${id}`}
              className="shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border border-[#C8553D] text-[#C8553D] hover:bg-red-50">
              <X className="w-3.5 h-3.5" />Remove
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function UserSearchAdd({ users, excludeIds, onPick }) {
  const [q, setQ] = useState("");
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t || !users) return [];
    return users
      .filter((u) => !excludeIds.includes(u.id) && `${u.display_name} ${u.username}`.toLowerCase().includes(t))
      .slice(0, 8);
  }, [q, users, excludeIds]);
  return (
    <div className="relative">
      <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#6B6B61]" />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="+ Add Member: naam / username type karo…"
        className="w-full pl-7 pr-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white" data-testid="ekyc-member-search" />
      {hits.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#DCDCD3] rounded shadow-lg max-h-64 overflow-y-auto" data-testid="ekyc-member-results">
          {hits.map((u) => (
            <div key={u.id} className="px-2.5 py-1.5 text-sm flex items-center gap-2 hover:bg-[#F5F5F0] min-w-0">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{u.display_name || u.username}</div>
                <div className="text-[11px] text-[#6B6B61] font-mono truncate">@{u.username}</div>
              </div>
              <RoleBadge role={u.role} />
              <button onClick={() => { onPick(u.id); setQ(""); }} data-testid={`ekyc-member-add-${u.id}`}
                className="shrink-0 flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded bg-[#385C4B] text-white hover:bg-[#2c4a3c]">
                <UserPlus className="w-3.5 h-3.5" />Add
              </button>
            </div>
          ))}
        </div>
      )}
      {q.trim() && users && hits.length === 0 && <div className="text-[11px] text-[#6B6B61] mt-1">Koi user nahi mila.</div>}
    </div>
  );
}

export function EkycMembersDialog({ group, onClose, onChanged }) {
  const { users, byId } = useEkycUsers();
  const ids = group.member_ids || [];
  const call = (method, uid, msg) =>
    api[method](`/ekyc/groups/${group.id}/members/${uid}`)
      .then(() => { toast.success(msg); onChanged(); })
      .catch((e) => toast.error(formatApiError(e)));
  const remove = (uid) => {
    const n = byId[uid]?.display_name || "member";
    if (window.confirm(`${n} ko "${group.name}" se remove karein?`)) call("delete", uid, `${n} removed`);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl w-[calc(100vw-2rem)] grid-cols-1" data-testid="ekyc-members-dialog">
        <DialogHeader className="min-w-0 pr-6">
          <DialogTitle className="truncate">{group.name}</DialogTitle>
          <div className="text-xs font-mono text-[#6B6B61]" data-testid="ekyc-members-counts">
            Members: {ids.length} · Admin: {Math.max(0, group.member_count - ids.length)} · Total: {group.member_count}
          </div>
        </DialogHeader>
        {users === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <div className="space-y-3 min-w-0">
            <UserSearchAdd users={users} excludeIds={ids} onPick={(uid) => call("post", uid, `${byId[uid]?.display_name || "Member"} added`)} />
            <MemberList ids={ids} byId={byId} onRemove={remove} />
            <div className="text-[11px] text-[#6B6B61]">EKYC Staff: Desk + Chat + Hisab. Baaki users: Chat (message + file) + Records sirf dekhna. Admin hamesha group me rehta hai.</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
